import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { formatBC } from "@/lib/format";
import { toast } from "sonner";
import { Radio, Search, RefreshCw, X, Activity } from "lucide-react";

function Dot({ online }) {
  return (
    <span className={`relative inline-block w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-400" : "bg-white/25"}`}>
      {online && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const sec = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function UserDetailModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setData(null); setErr(null);
    api.get(`/admin/users/${userId}/detail`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatErr(e)));
  }, [userId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-online-user-modal">
      <div className="glass-card gold-border max-w-3xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Operator Snapshot</div>
            <h2 className="font-display text-xl font-bold">{data?.user?.full_name || "—"}</h2>
            <div className="text-xs text-white/55">{data?.user?.email}</div>
          </div>
          <button onClick={onClose} className="text-white/65 hover:text-white" data-testid="admin-online-user-close"><X size={20} /></button>
        </div>
        {err && <div className="text-[#FCA5A5] text-sm">{err}</div>}
        {!data && !err && <div className="text-white/45 text-sm">Loading…</div>}
        {data && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Balance</div><div className="font-mono-num text-[#FBBF24] mt-1">₱{formatBC(data.user.active_balance)}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Locked</div><div className="font-mono-num mt-1">₱{formatBC(data.user.locked_assets)}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Withdrawal Lock</div><div className="font-mono-num mt-1">₱{formatBC(data.user.withdrawal_lock)}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Pending Cash-In</div><div className="font-mono-num text-[#93C5FD] mt-1">₱{formatBC(data.user.virtual_pending)}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Deposited Total</div><div className="font-mono-num mt-1">₱{formatBC(data.user.total_deposited)}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Withdrawn Total</div><div className="font-mono-num mt-1">₱{formatBC(data.user.total_withdrawn)}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">KYC</div><div className="mt-1 capitalize">{data.user.kyc_status}</div></div>
              <div className="bg-black/30 rounded p-3"><div className="text-[10px] uppercase tracking-widest text-white/45">Fraud Flags</div><div className={`mt-1 font-mono-num ${data.fraud_flags > 0 ? "text-[#FCA5A5]" : ""}`}>{data.fraud_flags}</div></div>
            </div>

            <div className="bg-black/30 rounded p-3 text-xs">
              <div className="text-[10px] uppercase tracking-widest text-white/45">Last seen</div>
              <div className="mt-1">{data.last_seen_at ? new Date(data.last_seen_at).toLocaleString() : "—"}</div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold mb-2">Recent ledger</div>
              {data.recent_ledger.length === 0 ? (
                <div className="text-white/40 text-xs">No ledger rows.</div>
              ) : (
                <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                  {data.recent_ledger.map((l, i) => (
                    <div key={l.id || `${l.timestamp}-${i}`} className="text-xs bg-white/[0.03] rounded px-2 py-1.5 flex justify-between gap-3">
                      <span className="text-white/75 font-mono">{l.action_type}</span>
                      <span className="text-white/40">{new Date(l.timestamp).toLocaleString()}</span>
                      <span className="font-mono-num text-[#FBBF24]">{l.amount ? `₱${formatBC(l.amount)}` : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold mb-1">Last deposits</div>
                {data.recent_deposits.length === 0 ? <div className="text-white/40 text-xs">None.</div>
                  : data.recent_deposits.slice(0, 5).map((d) => (
                    <div key={d.id} className="text-xs bg-white/[0.03] rounded px-2 py-1.5 mt-1 flex justify-between"><span>{d.reference_number}</span><span className="text-[#FBBF24]">₱{formatBC(d.amount)}</span><span className="text-white/40">{d.status}</span></div>
                  ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold mb-1">Last payouts</div>
                {data.recent_withdrawals.length === 0 ? <div className="text-white/40 text-xs">None.</div>
                  : data.recent_withdrawals.slice(0, 5).map((w) => (
                    <div key={w.id} className="text-xs bg-white/[0.03] rounded px-2 py-1.5 mt-1 flex justify-between"><span>{w.channel} → {w.account_number}</span><span className="text-[#FBBF24]">₱{formatBC(w.amount)}</span><span className="text-white/40">{w.status}</span></div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminOnline() {
  const [snapshot, setSnapshot] = useState(null);
  const [q, setQ] = useState("");
  const [showId, setShowId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/presence");
      setSnapshot(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    const h = setInterval(load, 15_000);
    return () => clearInterval(h);
  }, []);

  if (!snapshot) return <div className="text-white/55">Loading presence…</div>;
  const list = snapshot.active_users.filter((u) => {
    if (!q.trim()) return true;
    const n = q.trim().toLowerCase();
    return (u.email || "").toLowerCase().includes(n) || (u.full_name || "").toLowerCase().includes(n);
  });

  return (
    <div className="space-y-5" data-testid="admin-online-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4" data-testid="admin-online-stat-online"><div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1"><Radio size={11} className="text-emerald-400" /> Online now</div><div className="font-mono-num text-2xl text-emerald-300 mt-1">{snapshot.totals.online_now}</div><div className="text-[10px] text-white/40">last {Math.round(snapshot.totals.online_window_seconds / 60)} min</div></div>
        <div className="glass-card p-4"><div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1"><Activity size={11} /> Active 1h</div><div className="font-mono-num text-2xl text-[#FBBF24] mt-1">{snapshot.totals.active_last_hour}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] uppercase tracking-widest text-white/50">Active 24h</div><div className="font-mono-num text-2xl text-[#93C5FD] mt-1">{snapshot.totals.active_last_24h}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] uppercase tracking-widest text-white/50">Registered total</div><div className="font-mono-num text-2xl mt-1">{snapshot.totals.registered}</div></div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name…" className="zfd-input pl-7 text-xs py-1.5 w-full" data-testid="admin-online-search" />
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 inline-flex items-center gap-1" data-testid="admin-online-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
            <tr>
              <th className="text-left p-3">Status</th>
              <th className="text-left">Name</th>
              <th className="text-left">Email</th>
              <th className="text-left">Role</th>
              <th className="text-right">Balance</th>
              <th className="text-left">KYC</th>
              <th className="text-right pr-3">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="text-center text-white/40 py-6">No operators match.</td></tr>}
            {list.map((u) => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer" onClick={() => setShowId(u.id)} data-testid={`admin-online-row-${u.id}`}>
                <td className="p-3"><Dot online={u.is_online} /></td>
                <td className="text-xs">{u.full_name || "—"}</td>
                <td className="text-xs text-white/70">{u.email}</td>
                <td className="text-xs capitalize">{u.role}</td>
                <td className="text-xs text-right font-mono-num text-[#FBBF24]">₱{formatBC(u.active_balance)}</td>
                <td className="text-xs capitalize">{u.kyc_status}</td>
                <td className="text-xs text-right pr-3 text-white/60">{timeAgo(u.last_seen_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showId && <UserDetailModal userId={showId} onClose={() => setShowId(null)} />}
    </div>
  );
}
